import { Building2, UserPlus, Search, FileText, DollarSign } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { CompanySearchSelect } from '@/components/RocketSearch/CompanySearchSelect'
import {
  MODELOS,
  PLATAFORMAS,
  SEGMENTOS,
  type DealFormData,
} from '@/components/RocketSearch/rocketDealsFormTypes'
import type { EmpresaDetailed } from '@/types'

interface RocketDealsFormLeadFieldsProps {
  mode: 'new' | 'existing'
  form: DealFormData
  selectedCompany: EmpresaDetailed | null
  getError: (field: string) => string | undefined
  setField: <K extends keyof DealFormData>(key: K, value: DealFormData[K]) => void
  onCompanySelect: (company: EmpresaDetailed) => void
  onCompanyClear: () => void
}

export function RocketDealsFormLeadFields({
  mode,
  form,
  selectedCompany,
  getError,
  setField,
  onCompanySelect,
  onCompanyClear,
}: RocketDealsFormLeadFieldsProps) {
  if (mode === 'existing') {
    return (
      <div className='space-y-2'>
        <label className='font-medium text-[var(--font-color-label)] uppercase tracking-wider text-[11px]'>
          Selecionar Empresa
        </label>
        <CompanySearchSelect
          selectedCompany={selectedCompany}
          onSelect={onCompanySelect}
          onClear={onCompanyClear}
          error={undefined}
        />
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      <h4 className='text-xs font-semibold text-[var(--font-color-h3)] uppercase tracking-wider'>
        Dados do Lead
      </h4>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
        <Input
          label='Nome da Empresa *'
          placeholder='Razão social ou nome fantasia'
          value={form.empresaNome}
          onChange={(e) => setField('empresaNome', e.target.value)}
          error={getError('empresaNome')}
          icon={<Building2 size={13} />}
        />
        <Input
          label='CNPJ'
          placeholder='00.000.000/0000-00'
          value={form.cnpj}
          onChange={(e) => setField('cnpj', e.target.value)}
          icon={<Search size={13} />}
        />
        <Input
          label='Site'
          placeholder='https://'
          value={form.site}
          onChange={(e) => setField('site', e.target.value)}
          icon={<FileText size={13} />}
        />
        <Input
          label='Contato *'
          placeholder='Nome do contato'
          value={form.contato}
          onChange={(e) => setField('contato', e.target.value)}
          error={getError('contato')}
          icon={<UserPlus size={13} />}
        />
        <Input
          label='Telefone *'
          placeholder='(11) 99999-9999'
          value={form.telefone}
          onChange={(e) => setField('telefone', e.target.value)}
          error={getError('telefone')}
        />
        <Input
          label='Email'
          placeholder='contato@empresa.com'
          value={form.email}
          onChange={(e) => setField('email', e.target.value)}
        />
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
        <div className='flex flex-col gap-1.5'>
          <label className='font-medium text-[var(--font-color-label)] uppercase tracking-wider text-[11px]'>
            Segmento
          </label>
          <select
            value={form.segmento}
            onChange={(e) => setField('segmento', e.target.value)}
            className='w-full bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] rounded-md px-3 py-2 text-[13px] outline-none focus:border-[var(--brand-primary)]'
          >
            <option value=''>Selecione...</option>
            {SEGMENTOS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <div className='flex flex-col gap-1.5'>
          <label className='font-medium text-[var(--font-color-label)] uppercase tracking-wider text-[11px]'>
            Plataforma
          </label>
          <select
            value={form.plataforma}
            onChange={(e) => setField('plataforma', e.target.value)}
            className='w-full bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] rounded-md px-3 py-2 text-[13px] outline-none focus:border-[var(--brand-primary)]'
          >
            <option value=''>Selecione...</option>
            {PLATAFORMAS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        <div className='flex flex-col gap-1.5'>
          <label className='font-medium text-[var(--font-color-label)] uppercase tracking-wider text-[11px]'>
            Modelo de Negócio
          </label>
          <select
            value={form.modelo}
            onChange={(e) => setField('modelo', e.target.value)}
            className='w-full bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] rounded-md px-3 py-2 text-[13px] outline-none focus:border-[var(--brand-primary)]'
          >
            <option value=''>Selecione...</option>
            {MODELOS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        <Input
          label='Faturamento'
          placeholder='Ex: R$ 100K/mês'
          value={form.faturamento}
          onChange={(e) => setField('faturamento', e.target.value)}
          icon={<DollarSign size={13} />}
        />
      </div>
    </div>
  )
}
